import React, {useState} from "react";
import {FormattedHTMLMessage, FormattedMessage, useIntl} from "react-intl";
import { getMessageValue } from "@ignitesales/neuro-application/src/i18n/messageUtil";
import ReactSVG from "react-svg";
import Modal from "@ignitesales/react-modal";
import MerlinIcon from "./merlin/MerlinIcon";
import productActionsMap from "../utils/productActionsMap";
import PerfectScrollbar from "react-perfect-scrollbar";
import {useAppContext} from "../AppContext";
import TransitionContainer from "@ignitesales/neuro-application/src/components/TransitionContainer";
import {ConnectedContactFlow} from "@ignitesales/social-contact-appointment-form";
import getProductIcon from "../utils/getProductIcon";
import {notifySuccess, notifyInfo} from "./Notifications";

const ProductCart = ({
  answers,
  isOpen,
  onClose,
  onRemoveFromCartClick,
  productApplyClicked,
  selectedProducts,
}) => {
  const intl = useIntl();
  const [ showContactForm, setShowContactForm ] = useState(false);
  const [ leadTransmitted, setLeadTransmitted ] = useState(Object.hasOwnProperty.call(answers, 'leadTransmitted'));
  const [ urlAction, setUrlAction ] = useState('');
  const appContext = useAppContext();
  const cssBEM = "productCart";

  const header = (
    <div className={`${cssBEM}__header`}>
      <MerlinIcon />
      <FormattedMessage id="cart.modal.title" tagName="p" />
    </div>
  );

  const cartProductsGroupedByAction = Object.keys(productActionsMap).reduce((list, actionType) => {
    const filteredProducts = selectedProducts.filter(product => productActionsMap[actionType].find(p => p.id === product.id));
    const url = getMessageValue(intl.locale, `cart.${actionType}.url`);
    const urlParams = filteredProducts.map(product => {
      const mappedProduct = productActionsMap[actionType].find(p => p.id === product.id);
      return mappedProduct.clientId || mappedProduct.offerId || '';
    }).filter(id => !!id).join(',');

    return list.concat({
      type: actionType,
      products: filteredProducts,
      url: actionType === 'apply' || actionType === 'credit' ? `${url}${urlParams}&utm_source=merlin.stbank.com&utm_medium=referral#!/acctopening` : url
    })
  }, []);

  const renderCartProduct = (product) => {
    const icon = getProductIcon({ product });
    return (
      <div className="cart-product" key={product.id}>
        <ReactSVG src={icon} wrapper="span" />
        <h4>
          <FormattedHTMLMessage id={`${product.id}.name`} />
        </h4>
        <button onClick={() => onRemoveFromCartClick(product.id)}>
          <i className="fa fa-minus-circle" />
        </button>
      </div>
    )
  };

  const renderedCart = cartProductsGroupedByAction.map((group) => {
    return group.products.length ? (
      <div className={`${cssBEM}__products cart-group`} key={group.type}>
        <div className="cart-group__header">
          <FormattedMessage id={`cart.${group.type}`} tagName="h6" />
          <button className="btn" onClick={() => onCartActionClick(group)}>
            <FormattedMessage id={`cart.${group.type}.btn`} />
          </button>
        </div>
        {group.products.map(renderCartProduct)}
      </div>
    ) : null;
  });

  const onCartActionClick = ({ url, products, type }) => {
    (products || []).map( product => {
      //We want to trigger a "completed" action for each product under a group when the cart group action is clicked
      productApplyClicked({
        productId: product.id,
        completed: true,
        url
      })
    });

    if(leadTransmitted) {
      openCartActionUrl(url);
      onClose();
      type === 'contact' ? notifySuccess('contactForm') : notifyInfo('cartApplyClicked');
    } else {
      setShowContactForm(true);
      setUrlAction(url);
    }
  };

  const onFormSubmission = () => {
    openCartActionUrl(urlAction);
    setLeadTransmitted(true);
    setShowContactForm(false);
    onClose();
    notifySuccess('contactForm');
  }

  const openCartActionUrl = (url) => {
    if (url !== "#!") {
      window.open( url,  "_blank" );
    }
  }

  const renderedContactForm = <div className={`${cssBEM}__contactForm`}>
    <button className={`${cssBEM}__backButton`} onClick={() => setShowContactForm(false)}>
      <i className="fa fa-arrow-circle-o-left"/>
    </button>
    <FormattedHTMLMessage id='contactForm.message' tagName='p'/>
    <ConnectedContactFlow
      onComplete={ onFormSubmission }
      hasBeenClicked={ showContactForm }
      guideType="consumer"
    />
  </div>;

  return (
    <Modal
      show={isOpen}
      className={cssBEM}
      title={header}
      onClose={onClose}
      nodeId="root"
      closeBtn={appContext.modalCloseBtn}
    >
      <PerfectScrollbar>
        <TransitionContainer className="cartTransitionContainer" transitionKey={ showContactForm ? 'contact' : 'cart'}>
          <div>
            { showContactForm ? renderedContactForm : renderedCart }
          </div>
        </TransitionContainer>
      </PerfectScrollbar>
    </Modal>
  );
};

export default ProductCart;
